import { useState } from "react";
import { useLoaderData, useRevalidator, type LoaderFunctionArgs } from "react-router";
import { Loading, SettingsRowsSkeleton } from "../../components/skeletons.jsx";
import { Deferred, EmptyState } from "../../components/states.jsx";
import { useI18n } from "../../i18n/index.js";
import type { WorkspaceDetail } from "../../lib/api.js";
import { authClient } from "../../lib/auth-client.js";
import { deferred } from "../../lib/deferred.js";
import { apiContext } from "../../lib/session.js";
import { workspaceContext } from "../../lib/workspace.js";
import { Badge, Button, Dialog, FieldGroup, FormError, PageHeader, PlusIcon, Select, SettingsBlock, SettingsRow, SettingsSection, TextField, UsersIcon, useToast, Skeleton } from "../../ui/index.js";

export function membersLoader({ context }: LoaderFunctionArgs) {
  const ws = context.get(workspaceContext);
  return { detail: deferred(context.get(apiContext).workspace(ws.id).get()) };
}

const ROLE_VALUES = ["member", "admin"] as const;

function InviteDialog({ organizationId, onInvited }: { organizationId: string; onInvited: () => void }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [role, setRole] = useState<"member" | "admin">("member");
  const { t } = useI18n();
  const toast = useToast();
  const roles = ROLE_VALUES.map((value) => ({ value, label: t(`role.${value}`) }));
  return (
    <Dialog.Root
      open={open}
      onOpenChange={(next) => {
        if (busy) return;
        setOpen(next);
        if (!next) setError(null);
      }}
    >
      <Dialog.Trigger render={<Button variant="primary" icon={<PlusIcon />} />}>{t("members.invite")}</Dialog.Trigger>
      <Dialog.Content title={t("members.invite.title")} description={t("members.invite.description")}>
        <form
          id="invite-form"
          className="dt-form"
          onSubmit={async (e) => {
            e.preventDefault();
            const data = new FormData(e.currentTarget);
            const email = String(data.get("email"));
            setBusy(true);
            setError(null);
            const { error } = await authClient.organization.inviteMember({ email, role, organizationId });
            setBusy(false);
            if (error) setError(error.message ?? t("members.invite.failed"));
            else {
              setOpen(false);
              toast.success(t("members.invite.sent"), email);
              onInvited();
            }
          }}
        >
          <TextField name="email" label={t("field.email")} type="email" placeholder={t("members.invite.emailPlaceholder")} required autoFocus />
          <FieldGroup name="role" label={t("members.invite.role")} description={t("members.invite.roleHint")}>
            <Select items={roles} value={role} onValueChange={setRole} aria-label={t("members.invite.roleAria")} />
          </FieldGroup>
          <FormError message={error} />
          <div className="dt-form__actions" style={{ justifyContent: "flex-end" }}>
            <Dialog.Close render={<Button variant="ghost" />} disabled={busy}>
              {t("common.cancel")}
            </Dialog.Close>
            <Button type="submit" variant="primary" loading={busy}>
              {t("members.invite.send")}
            </Button>
          </div>
        </form>
      </Dialog.Content>
    </Dialog.Root>
  );
}

export function MembersSkeleton() {
  const { t } = useI18n();
  return (
    <Loading>
      <PageHeader title={t("members.title")} description={<Skeleton width={80} />} actions={<Skeleton shape="button" width={88} />} />
      <SettingsSection>
        <SettingsRowsSkeleton count={4} control={<Skeleton shape="badge" />} />
      </SettingsSection>
    </Loading>
  );
}

export function MembersRoute() {
  const { detail } = useLoaderData<typeof membersLoader>();
  // The description counts members, so the header waits with the list.
  return (
    <Deferred data={detail} fallback={<MembersSkeleton />}>
      {(ws) => <Members ws={ws} />}
    </Deferred>
  );
}

function Members({ ws }: { ws: WorkspaceDetail }) {
  const revalidator = useRevalidator();
  const { t, tOr, tPlural, formatDate } = useI18n();
  return (
    <>
      <PageHeader
        title={t("members.title")}
        description={tPlural("members.count", ws.members.length)}
        actions={<InviteDialog organizationId={ws.id} onInvited={() => revalidator.revalidate()} />}
      />
      <SettingsSection>
        {ws.members.length === 0 ? (
          <SettingsBlock>
            <EmptyState title={t("members.empty.title")} icon={<UsersIcon size={18} />} />
          </SettingsBlock>
        ) : (
          ws.members.map((m) => (
            <SettingsRow key={m.id} label={m.name} description={m.email}>
              <Badge>{tOr(`role.${m.role}`, m.role)}</Badge>
            </SettingsRow>
          ))
        )}
      </SettingsSection>
      {ws.pendingInvitations.length > 0 && (
        <SettingsSection title={t("members.pending.title")} description={t("members.pending.description")}>
          {ws.pendingInvitations.map((i) => (
            <SettingsRow key={i.id} label={i.email} description={t("members.pending.expires", { date: formatDate(i.expiresAt) })}>
              <Badge>{tOr(`role.${i.role}`, i.role)}</Badge>
            </SettingsRow>
          ))}
        </SettingsSection>
      )}
    </>
  );
}
